/**
 * Grouping words into cues, and deciding how long each one stays on screen.
 *
 * Three layers, in order, because each depends on the one before it: group
 * words into cues that fit the budget, merge away anything too short to read,
 * then give every word its highlight span.
 */

import { rstrip, sum } from "./py";
import { MAX_CAPTION_LINES, type Measure, breakLines, isDangling, measureCharacters } from "./shaping";
import { type Milliseconds, ms } from "./units";
import type { Word } from "./words";

export const MAX_WORDS_PER_CUE = 6;
export const SILENCE_BREAK_MS = ms(400);
export const MINIMUM_CUE_MS = ms(500);

/**
 * How far past its last word a cue may hold while nobody is speaking: long
 * enough to bridge short pauses without flicker, short enough not to leave a
 * stale caption sitting through a long one.
 */
export const MAX_LINGER_MS = ms(1000);

const SENTENCE_FINAL = [".", "!", "?", "…"];
const TRAILING = "»\"')";

/** A group of words shown together, already broken into lines. */
export interface Cue {
  readonly words: readonly Word[];
  readonly lines: readonly (readonly string[])[];
}

export const cueStart = (cue: Cue): Milliseconds => cue.words[0]?.start ?? ms(0);
export const cueStop = (cue: Cue): Milliseconds => cue.words[cue.words.length - 1]?.end ?? ms(0);
export const cueText = (cue: Cue): string => cue.words.map((w) => w.text).join(" ");

function endsSentence(text: string): boolean {
  const trimmed = rstrip(text, TRAILING);
  return SENTENCE_FINAL.some((mark) => trimmed.endsWith(mark));
}

function fits(words: readonly Word[], budget: number, measure: Measure): boolean {
  const lines = breakLines(
    words.map((w) => w.text),
    budget,
    MAX_CAPTION_LINES,
    undefined,
    measure,
  );
  if (lines.length > MAX_CAPTION_LINES) return false;
  return lines.every((line) => sum(line.map(measure)) + Math.max(line.length - 1, 0) * measure(" ") <= budget);
}

/** Accumulate words into runs, breaking where the text or the pauses say to. */
function splitRuns(words: readonly Word[], budget: number, measure: Measure): Word[][] {
  const groups: Word[][] = [];
  let current: Word[] = [];
  for (const word of words) {
    const previous = current[current.length - 1];
    if (previous !== undefined) {
      const gap = word.start - previous.end;
      const wantsBreak = gap > SILENCE_BREAK_MS || endsSentence(previous.text);
      // A cue boundary is the harder break, so the rule against ending on a
      // word that belongs to the next one applies with more force here.
      const forced = wantsBreak && !isDangling(previous.text);
      const overBudget = current.length >= MAX_WORDS_PER_CUE || !fits([...current, word], budget, measure);
      if (forced || overBudget) {
        groups.push(current);
        current = [];
      }
    }
    current.push(word);
  }
  if (current.length) groups.push(current);
  return groups;
}

/** Join anything too short to read into what follows, while it still fits. */
function mergeFlashes(groups: readonly Word[][], budget: number, measure: Measure): Word[][] {
  const merged: Word[][] = [];
  let index = 0;
  while (index < groups.length) {
    let group = groups[index] ?? [];
    for (;;) {
      const next = groups[index + 1];
      const first = group[0];
      const last = group[group.length - 1];
      if (first === undefined || last === undefined || next === undefined) break;
      if (!(last.end - first.start < MINIMUM_CUE_MS && fits([...group, ...next], budget, measure))) break;
      index += 1;
      group = [...group, ...next];
    }
    merged.push(group);
    index += 1;
  }
  return merged;
}

/**
 * Words in, cues out: budgeted, readable, and already broken into lines.
 * `budget` is a width and `measure` says how to compute one; the default
 * measures in characters, which is what a monospace face does.
 */
export function groupCues(words: readonly Word[], budget: number, measure: Measure = measureCharacters): Cue[] {
  if (!words.length) return [];
  return mergeFlashes(splitRuns(words, budget, measure), budget, measure).map((group) => ({
    words: group,
    lines: breakLines(
      group.map((w) => w.text),
      budget,
      MAX_CAPTION_LINES,
      undefined,
      measure,
    ),
  }));
}

/** When a cue leaves the screen: it may linger, never into the next cue. */
export function cueEnd(cues: readonly Cue[], index: number, videoDurationMs: number): Milliseconds {
  const cue = cues[index];
  if (cue === undefined) throw new RangeError(`no cue ${index}`);
  const next = cues[index + 1];
  const ceiling = next !== undefined ? cueStart(next) : videoDurationMs;
  const wanted = Math.min(cueStop(cue) + MAX_LINGER_MS, ceiling);
  // The readability floor wins over the linger cap, but never over the ceiling.
  const floor = cueStart(cue) + MINIMUM_CUE_MS;
  return ms(Math.max(Math.min(Math.max(wanted, floor), ceiling), cueStop(cue)));
}

/** Each word holds the accent until the next begins; the last until `end`. */
export function highlightSpans(cue: Cue, end: number): [string, Milliseconds, Milliseconds][] {
  return cue.words.map((word, position) => {
    const following = cue.words[position + 1]?.start ?? end;
    return [word.text, word.start, ms(Math.max(following, word.start))];
  });
}
